import type { IChannelAllocation, IChannelBudgets } from '@feathr/blackbox';

export const MIN_SPLIT = 5;
export const MAX_SPLIT = 95;

/** Both Display (TTD) and Meta require at least $5/day on their channel. */
export const CHANNEL_MIN_DAILY_BUDGET = 5;

/**
 * Default Display (Tradedesk) percentage before the backend recommendation
 * loads: an even 50/50 split. The backend's recommended split (which already
 * respects the per-channel minimums) is used once available.
 */
export const RECOMMENDED_SPLIT_DEFAULT = 50;

/**
 * Find the percentage for a given channel in the allocations array.
 *
 * @param allocations - Array of channel allocation objects.
 * @param channel - The channel identifier to look up.
 * @returns The percentage for the channel, or undefined if not found.
 */
export function getAllocationPercentage(
  allocations: IChannelAllocation[] | undefined,
  channel: IChannelAllocation['channel'],
): number | undefined {
  return allocations?.find((a) => a.channel === channel)?.percentage;
}

/** Parameters for {@link isSplitBelowFloor}. */
interface IChannelSplitFloorParams {
  /** Total dollars allocated to the display channel. */
  displayBudget: number;
  /** Total dollars allocated to the meta channel. */
  metaBudget: number;
  /** Backend per-channel minimum budgets (total dollars per channel). */
  channelMinimums?: IChannelBudgets | null;
}

/**
 * Whether the split leaves either channel below its backend minimum. Both
 * Display and Meta are enforced. At (or below) the minimum budget the check is
 * skipped: the split is locked to the valid recommended allocation there
 * (whole-number percentages can't hit each channel's exact minimum) and the
 * budget-minimum error covers an insufficient total.
 *
 * Returns false until the backend per-channel minimums have loaded — the
 * frontend has no representative estimate of its own, so it never guesses (a
 * $5/day-style fallback misstates the real per-channel minimums). Both
 * `validateStepBudget` and the inline alert use this so they stay in lockstep.
 */
export function isSplitBelowFloor({
  displayBudget,
  metaBudget,
  channelMinimums,
}: IChannelSplitFloorParams): boolean {
  if (!channelMinimums) {
    return false;
  }
  if (displayBudget + metaBudget <= channelMinimums.total) {
    return false;
  }
  return displayBudget < channelMinimums.display || metaBudget < channelMinimums.meta;
}

/**
 * Calculate the recommended channel split as whole-number percentages summing
 * to 100 (the backend rejects fractional allocations).
 *
 * Uses the backend-recommended Display percentage, defaulting to an even 50/50
 * split before it loads. The backend already accounts for the per-channel
 * minimums, so we don't re-derive or clamp by them here — MIN_SPLIT/MAX_SPLIT
 * are only the slider's drag bounds, applied defensively.
 *
 * @param recommendedTradedesk - Backend-recommended Display percentage (whole
 *   number); defaults to 50 when the recommendation hasn't loaded.
 * @returns Object with tradedesk and meta percentages summing to 100.
 */
export function calculateRecommendedSplit(recommendedTradedesk?: number | null): {
  tradedesk: number;
  meta: number;
} {
  const rawTradedesk = recommendedTradedesk ?? RECOMMENDED_SPLIT_DEFAULT;
  const tradedesk = Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, rawTradedesk));
  return { tradedesk, meta: 100 - tradedesk };
}

/**
 * Calculate per-channel dollar amounts from total budget and display percentage.
 *
 * @param totalBudget - Total campaign budget in dollars.
 * @param displayPercent - Integer percentage allocated to display (0-100).
 * @returns Object with displayBudget and metaBudget that sum to totalBudget.
 */
export function calculateChannelBudgets(
  totalBudget: number,
  displayPercent: number,
): { displayBudget: number; metaBudget: number } {
  const displayBudget = Math.round(((totalBudget * displayPercent) / 100) * 100) / 100;
  const metaBudget = Math.round((totalBudget - displayBudget) * 100) / 100;
  return { displayBudget, metaBudget };
}
